import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ArrowUpRight, FolderInput, KeyRound, SlidersHorizontal, Mail, X, Shuffle, Code2, FileCode2, FileUp, Globe, ShieldCheck, ChevronDown, ChevronRight, RefreshCw, CheckCircle2, AlertCircle, Clock, CalendarClock, AlertTriangle, RotateCw, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { SITES } from "@/constants/testIds";
import { prefetch, takePrefetched, readCache, writeCache } from "@/lib/swr";
import FolderRail from "./sites/FolderRail";
import { MoveToFolderModal, SparkPostKeyModal, ContentSettingsModal, EmailRebindModal, ShuffleLooksModal, HeadScriptModal, FormSnippetModal, RootFileModal, DnsRecordModal, PostmasterTxtModal, GoogleWorkspaceModal, SafeSourcesModal } from "./sites/BulkModals";
import DeleteSitesModal from "./sites/DeleteSitesModal";

const STATUS_DOT = {
  live: "#10b981",
  draft: "#f59e0b",
  paused: "#71717a",
  archived: "#52525b",
};

const GRID = "grid grid-cols-[36px_1fr_120px_90px_120px_130px_110px_50px] items-center";
const SITES_URL = "/sites?view=list";
const EXPIRING_LEVELS = new Set(["expired", "critical"]);
const listFailMsg = (e) => e?.response?.data?.detail || "Couldn't refresh the sites list — showing the last known state";

/** Fire the list requests at boot so they run in parallel with the session check. */
export function prefetchSitesList() {
  prefetch("sites", () => api.get(SITES_URL));
  prefetch("folders", () => api.get("/folders"));
}

export default function SitesList() {
  // Paint the last known list instantly (stale-while-revalidate), then refresh.
  const [sites, setSites] = useState(() => readCache("sites"));
  const [folderData, setFolderData] = useState(() => readCache("folders") || { folders: [], unfiled_count: 0, total_sites: 0 });
  const [active, setActive] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const [modal, setModal] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [rechecking, setRechecking] = useState(() => new Set());
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [refreshingExpiry, setRefreshingExpiry] = useState(false);
  const [expiryMsg, setExpiryMsg] = useState("");
  const [listErr, setListErr] = useState("");
  const [recheckErr, setRecheckErr] = useState({});
  const nav = useNavigate();

  async function reload() {
    const [s, f] = await Promise.all([
      takePrefetched("sites") || api.get(SITES_URL),
      takePrefetched("folders") || api.get("/folders"),
    ]);
    if (s?.data) { setSites(s.data); writeCache("sites", s.data); }
    else if (!s) { const r = await api.get(SITES_URL); setSites(r.data); writeCache("sites", r.data); }
    if (f?.data) { setFolderData(f.data); writeCache("folders", f.data); }
    else if (!f) { const r = await api.get("/folders"); setFolderData(r.data); writeCache("folders", r.data); }
    setListErr("");
  }
  async function reloadSites() {
    try { const s = await api.get(SITES_URL); setSites(s.data); writeCache("sites", s.data); setListErr(""); }
    catch (e) { setListErr(listFailMsg(e)); }
  }
  useEffect(() => { reload().catch((e) => setListErr(listFailMsg(e))); }, []);

  // Auto-poll the (cheap, stored-status) sites list while any domain is still
  // "setting up", and auto-STOP once everything is green. No external API calls
  // per poll — the live re-verification happens in the background sweep.
  useEffect(() => {
    if (!sites) return;
    const anyPending = sites.some((s) => s.readiness && s.readiness.overall === "pending");
    if (!anyPending) return;
    const t = setTimeout(reloadSites, 20000);
    return () => clearTimeout(t);
  }, [sites]);

  function toggleExpand(id) {
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  async function recheck(siteId) {
    setRechecking((prev) => new Set(prev).add(siteId));
    setRecheckErr((prev) => ({ ...prev, [siteId]: "" }));
    try {
      const r = await api.post(`/sites/${siteId}/recheck`);
      setSites((prev) => (prev || []).map((s) => (s.id === siteId ? { ...s, ...r.data } : s)));
    } catch (e) {
      setRecheckErr((prev) => ({ ...prev, [siteId]: e?.response?.data?.detail || "Re-check failed — try again in a moment" }));
    } finally {
      setRechecking((prev) => { const n = new Set(prev); n.delete(siteId); return n; });
    }
  }

  const foldersById = useMemo(() => {
    const m = {};
    (folderData.folders || []).forEach((f) => { m[f.id] = f; });
    return m;
  }, [folderData]);

  const filtered = useMemo(() => {
    let list = sites || [];
    if (active === "unfiled") list = list.filter((s) => !s.folder_id);
    else if (active !== "all") list = list.filter((s) => s.folder_id === active);
    if (expiringOnly) list = list.filter((s) => s.expiry && EXPIRING_LEVELS.has(s.expiry.level));
    return list;
  }, [sites, active, expiringOnly]);

  // Renewal watch: domains expired or inside the 30-day window, soonest first.
  const expiring = useMemo(() => (sites || [])
    .filter((s) => s.expiry && EXPIRING_LEVELS.has(s.expiry.level))
    .sort((a, b) => (a.expiry.days_left ?? 0) - (b.expiry.days_left ?? 0)), [sites]);
  const unknownExpiry = useMemo(() => (sites || []).filter((s) => s.domain && s.expiry && s.expiry.level === "unknown").length, [sites]);

  async function refreshExpiry() {
    setRefreshingExpiry(true); setExpiryMsg("");
    try {
      const r = await api.post("/domains/expiry-refresh");
      const src = r.data.sources || {};
      const parts = [`${r.data.refreshed} updated`];
      if (src.namecheap) parts.push(`Namecheap ${src.namecheap.ok ? `${src.namecheap.domains} domains` : "unavailable"}`);
      if (src.cloudflare) parts.push(`Cloudflare ${src.cloudflare.ok ? `${src.cloudflare.domains} domains` : "unavailable"}`);
      if (r.data.rdap_checked) parts.push(`RDAP ${r.data.rdap_ok}/${r.data.rdap_checked} resolved`);
      setExpiryMsg(parts.join(" · "));
      await reloadSites();
    } catch (e) { setExpiryMsg(e?.response?.data?.detail || "Refresh failed"); }
    finally { setRefreshingExpiry(false); }
  }

  const filteredIds = filtered.map((s) => s.id);
  const allVisibleSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));

  function toggle(id) {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleAll() {
    setSelected((prev) => {
      const n = new Set(prev);
      if (allVisibleSelected) filteredIds.forEach((id) => n.delete(id));
      else filteredIds.forEach((id) => n.add(id));
      return n;
    });
  }
  function clearSel() { setSelected(new Set()); }

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">All Sites</h1>
          <div className="text-zinc-500 text-sm mt-1">
            {sites === null ? "Loading…" : `${folderData.total_sites} site${folderData.total_sites === 1 ? "" : "s"} · ${folderData.folders.length} folder${folderData.folders.length === 1 ? "" : "s"}`}
          </div>
          {listErr && <div data-testid="sites-list-error" className="text-[11px] mono text-rose-400 mt-1">{listErr}</div>}
        </div>
        <button
          data-testid={SITES.newButton}
          onClick={() => nav("/admin/sites/new")}
          className="inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-zinc-200 transition-colors px-4 py-2 rounded-sm text-sm font-semibold"
        >
          <Plus size={14} /> New Site
        </button>
      </div>

      <div className="flex gap-6 mt-6">
        <FolderRail
          folders={folderData.folders}
          unfiledCount={folderData.unfiled_count}
          totalSites={folderData.total_sites}
          active={active}
          onSelect={setActive}
          reload={reload}
        />

        <div className="flex-1 min-w-0">
          <ExpiryAlert
            expiring={expiring}
            unknownCount={unknownExpiry}
            filterOn={expiringOnly}
            onToggleFilter={() => setExpiringOnly((v) => !v)}
            onRefresh={refreshExpiry}
            refreshing={refreshingExpiry}
            msg={expiryMsg}
          />
          {selectedIds.length > 0 && (
            <div data-testid="bulk-bar" className="flex items-center gap-2 flex-wrap mb-3 px-4 py-2.5 border border-amber-500/30 bg-amber-500/5 rounded-sm">
              <span className="text-sm font-semibold text-amber-200 mr-1" data-testid="bulk-selected-count">{selectedIds.length} selected</span>
              <button data-testid="bulk-action-move" onClick={() => setModal("move")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-200 hover:text-white border border-zinc-700 hover:border-zinc-500 px-2.5 py-1.5 rounded-sm"><FolderInput size={13} /> Move to folder</button>
              <button data-testid="bulk-action-shuffle" onClick={() => setModal("shuffle")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-200 hover:text-white border border-amber-500/40 hover:border-amber-400 bg-amber-500/10 px-2.5 py-1.5 rounded-sm"><Shuffle size={13} /> Shuffle distinct looks</button>
              <button data-testid="bulk-action-sparkpost" onClick={() => setModal("sparkpost")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-200 hover:text-white border border-zinc-700 hover:border-zinc-500 px-2.5 py-1.5 rounded-sm"><KeyRound size={13} /> Set SparkPost key</button>
              <button data-testid="bulk-action-safe-sources" onClick={() => setModal("safe-sources")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-200 hover:text-white border border-emerald-500/40 hover:border-emerald-400 bg-emerald-500/10 px-2.5 py-1.5 rounded-sm"><ShieldCheck size={13} /> Safe sources</button>
              <button data-testid="bulk-action-content" onClick={() => setModal("content")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-200 hover:text-white border border-zinc-700 hover:border-zinc-500 px-2.5 py-1.5 rounded-sm"><SlidersHorizontal size={13} /> Content settings</button>
              <button data-testid="bulk-action-scripts" onClick={() => setModal("scripts")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-200 hover:text-white border border-zinc-700 hover:border-zinc-500 px-2.5 py-1.5 rounded-sm"><Code2 size={13} /> Scripts</button>
              <button data-testid="bulk-action-form-snippet" onClick={() => setModal("form-snippet")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-200 hover:text-white border border-zinc-700 hover:border-zinc-500 px-2.5 py-1.5 rounded-sm"><FileCode2 size={13} /> Form snippet</button>
              <button data-testid="bulk-action-root-file" onClick={() => setModal("root-file")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-200 hover:text-white border border-zinc-700 hover:border-zinc-500 px-2.5 py-1.5 rounded-sm"><FileUp size={13} /> Root file</button>
              <button data-testid="bulk-action-dns" onClick={() => setModal("dns")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-200 hover:text-white border border-zinc-700 hover:border-zinc-500 px-2.5 py-1.5 rounded-sm"><Globe size={13} /> DNS</button>
              <button data-testid="bulk-action-postmaster" onClick={() => setModal("postmaster")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-200 hover:text-white border border-zinc-700 hover:border-zinc-500 px-2.5 py-1.5 rounded-sm"><ShieldCheck size={13} /> Postmaster</button>
              <button data-testid="bulk-action-email" onClick={() => setModal("email")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-200 hover:text-white border border-zinc-700 hover:border-zinc-500 px-2.5 py-1.5 rounded-sm"><Mail size={13} /> Rebind email</button>
              <button data-testid="bulk-action-gws" onClick={() => setModal("gws")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-200 hover:text-white border border-sky-500/40 hover:border-sky-400 bg-sky-500/10 px-2.5 py-1.5 rounded-sm"><Mail size={13} /> Google Workspace</button>
              <button data-testid="bulk-action-delete" onClick={() => setModal("delete")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-200 hover:text-white border border-rose-500/40 hover:border-rose-400 bg-rose-500/10 px-2.5 py-1.5 rounded-sm"><Trash2 size={13} /> Delete</button>
              <button data-testid="bulk-clear" onClick={clearSel} className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white ml-auto"><X size={13} /> Clear</button>
            </div>
          )}

          <div data-testid={SITES.table} className="border border-zinc-800 rounded-sm overflow-hidden">
            <div className={`${GRID} bg-zinc-900/70 border-b border-zinc-800 label-tiny px-4 py-2`}>
              <div>
                <input type="checkbox" data-testid="bulk-select-all" checked={allVisibleSelected} onChange={toggleAll} />
              </div>
              <div>Site / Domain</div>
              <div>Template</div>
              <div>Articles</div>
              <div>AI Spend Today</div>
              <div className="inline-flex items-center gap-1"><CalendarClock size={11} /> Expires</div>
              <div>Status</div>
              <div></div>
            </div>
            <div className="divide-y divide-zinc-800">
              {filtered.map((s) => {
                const folder = s.folder_id ? foldersById[s.folder_id] : null;
                const isExp = expanded.has(s.id);
                return (
                  <React.Fragment key={s.id}>
                  <div
                    data-testid={SITES.row(s.id)}
                    className={`${GRID} px-4 py-3 transition-colors ${selected.has(s.id) ? "bg-amber-500/5" : "hover:bg-zinc-900/40"}`}
                  >
                    <div>
                      <input type="checkbox" data-testid={`site-select-${s.id}`} checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Link to={`/admin/sites/${s.id}`} className="text-sm font-medium text-zinc-100 hover:text-white truncate">{s.name}</Link>
                        {folder && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm border border-zinc-700 text-zinc-400" title={`Folder: ${folder.name}`}>
                            <span className="dot" style={{ background: folder.color }} />{folder.name}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="mono text-[11px] text-zinc-500 truncate">{s.domain || `/site/${s.slug}`}</span>
                        {s.domain && s.email_health && <EmailHealthBadge health={s.email_health} />}
                      </div>
                      <ReadinessDots r={s.readiness} expanded={isExp} onToggle={() => toggleExpand(s.id)} />
                    </div>
                    <div>
                      {s.site_type === "static" ? (
                        <span data-testid={`site-type-static-${s.id}`} title={s.has_form_embed ? "Form snippet set" : "No form snippet yet"} className={`inline-block border px-2 py-0.5 rounded-sm text-[11px] mono uppercase tracking-wider ${s.has_form_embed ? "border-sky-500/50 text-sky-300" : "border-amber-500/50 text-amber-300"}`}>static · {s.static_kind || "loan"}</span>
                      ) : (
                        <span className="inline-block border border-zinc-700 px-2 py-0.5 rounded-sm text-[11px] mono uppercase tracking-wider text-zinc-300">{s.template}/{s.variant}</span>
                      )}
                    </div>
                    <div className="mono text-sm text-zinc-200">{s.site_type === "static" ? <span className="text-zinc-600">—</span> : (s.articles_count || 0)}</div>
                    <div className="mono text-sm text-zinc-200">${(s.ai_spend_today_usd || 0).toFixed(3)}</div>
                    <ExpiryCell e={s.expiry} siteId={s.id} hasDomain={Boolean(s.domain)} />
                    <div className="flex items-center gap-2">
                      <span className="dot" style={{ background: STATUS_DOT[s.status] || "#71717a" }} />
                      <span className="text-xs text-zinc-300 capitalize">{s.status}</span>
                    </div>
                    <div className="text-right">
                      <Link data-testid={SITES.rowOpen(s.id)} to={`/admin/sites/${s.id}`} className="inline-flex items-center text-zinc-400 hover:text-white"><ArrowUpRight size={16} /></Link>
                    </div>
                  </div>
                  {isExp && s.readiness && (
                    <ReadinessPanel r={s.readiness} rechecking={rechecking.has(s.id)} onRecheck={() => recheck(s.id)} error={recheckErr[s.id]} />
                  )}
                  </React.Fragment>
                );
              })}
              {sites !== null && filtered.length === 0 && (
                <div className="px-4 py-10 text-center text-zinc-500 text-sm">
                  {active === "all"
                    ? <>No sites yet. <Link to="/admin/sites/new" className="text-white underline">Create the first one</Link>.</>
                    : "No sites in this view."}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {modal === "move" && <MoveToFolderModal folders={folderData.folders} siteIds={selectedIds} onClose={() => setModal(null)} onDone={reload} />}
      {modal === "shuffle" && <ShuffleLooksModal siteIds={selectedIds} onClose={() => setModal(null)} onDone={reload} />}
      {modal === "sparkpost" && <SparkPostKeyModal siteIds={selectedIds} onClose={() => setModal(null)} onDone={reload} />}
      {modal === "content" && <ContentSettingsModal siteIds={selectedIds} onClose={() => setModal(null)} onDone={reload} />}
      {modal === "safe-sources" && <SafeSourcesModal siteIds={selectedIds} onClose={() => setModal(null)} onDone={reload} />}
      {modal === "scripts" && <HeadScriptModal siteIds={selectedIds} onClose={() => setModal(null)} onDone={reload} />}
      {modal === "form-snippet" && <FormSnippetModal siteIds={selectedIds} onClose={() => setModal(null)} onDone={reload} />}
      {modal === "root-file" && <RootFileModal siteIds={selectedIds} onClose={() => setModal(null)} onDone={reload} />}
      {modal === "dns" && <DnsRecordModal siteIds={selectedIds} onClose={() => setModal(null)} onDone={reload} />}
      {modal === "postmaster" && <PostmasterTxtModal siteIds={selectedIds} onClose={() => setModal(null)} onDone={reload} />}
      {modal === "email" && <EmailRebindModal siteIds={selectedIds} onClose={() => setModal(null)} onDone={reload} />}
      {modal === "gws" && <GoogleWorkspaceModal siteIds={selectedIds} onClose={() => setModal(null)} onDone={reload} />}
      {modal === "delete" && <DeleteSitesModal siteIds={selectedIds} onClose={() => setModal(null)} onDone={async () => { clearSel(); await reload(); }} />}
    </div>
  );
}

// ------------------------------------------------------------ renewal watch
const EXPIRY_STYLE = {
  expired: { chip: "text-rose-300 border-rose-500/40 bg-rose-500/15", text: "text-rose-400" },
  critical: { chip: "text-rose-400 border-rose-500/30 bg-rose-500/10", text: "text-rose-400" },
  warning: { chip: "text-amber-400 border-amber-500/30 bg-amber-500/10", text: "text-amber-400" },
  ok: { chip: "text-zinc-400 border-zinc-700", text: "text-zinc-300" },
  unknown: { chip: "text-zinc-600 border-zinc-800", text: "text-zinc-600" },
};

function daysLabel(d) {
  if (d == null) return "";
  const n = Math.round(d);
  if (n < 0) return `expired ${Math.abs(n)}d ago`;
  if (n === 0) return "expires today";
  return `in ${n}d`;
}

function ExpiryCell({ e, siteId, hasDomain }) {
  if (!hasDomain || !e) return <div className="mono text-xs text-zinc-700">—</div>;
  const st = EXPIRY_STYLE[e.level] || EXPIRY_STYLE.unknown;
  if (e.level === "unknown") {
    return (
      <div data-testid={`expiry-${siteId}`} className="mono text-[11px] text-zinc-600" title="No expiry data yet — use “Refresh expiries” (registrar inventory or public RDAP)">
        unknown
      </div>
    );
  }
  const src = e.source ? ` · via ${e.source}` : "";
  const renew = e.auto_renew === true ? " · auto-renew ON" : e.auto_renew === false ? " · auto-renew OFF" : "";
  return (
    <Link
      to={`/admin/sites/${siteId}?tab=domain`}
      data-testid={`expiry-${siteId}`}
      className="block min-w-0"
      title={`Expires ${(e.expires_at || "").slice(0, 10)}${src}${renew}${e.checked_at ? ` · checked ${e.checked_at.slice(0, 10)}` : ""}`}
    >
      <div className={`mono text-xs ${st.text}`}>{(e.expires_at || "").slice(0, 10)}</div>
      <div className="flex items-center gap-1 mt-0.5">
        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm border ${st.chip}`}>
          {(e.level === "expired" || e.level === "critical") && <AlertTriangle size={9} />}{daysLabel(e.days_left)}
        </span>
        {e.auto_renew === true && <span className="text-[10px] text-emerald-500" title="Registrar auto-renew is ON"><RotateCw size={10} /></span>}
      </div>
    </Link>
  );
}

function ExpiryAlert({ expiring, unknownCount, filterOn, onToggleFilter, onRefresh, refreshing, msg }) {
  const refreshBtn = (
    <button
      data-testid="expiry-refresh-btn"
      onClick={onRefresh}
      disabled={refreshing}
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-sm border border-zinc-700 text-zinc-300 hover:border-zinc-400 hover:text-white disabled:opacity-50"
      title="Pull expiry dates from Namecheap + Cloudflare Registrar, RDAP for everything else"
    >
      <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh expiries"}
    </button>
  );
  if (expiring.length === 0) {
    if (!unknownCount && !msg) return null;
    return (
      <div data-testid="expiry-quiet" className="flex items-center gap-3 mb-3 text-[11px] text-zinc-500">
        {unknownCount > 0 && <span>{unknownCount} domain{unknownCount === 1 ? "" : "s"} without expiry data</span>}
        {msg && <span className="mono text-zinc-400" data-testid="expiry-msg">{msg}</span>}
        <span className="ml-auto">{refreshBtn}</span>
      </div>
    );
  }
  const expiredCount = expiring.filter((s) => s.expiry.level === "expired").length;
  return (
    <div data-testid="expiry-alert" className="mb-3 px-4 py-3 border border-rose-500/40 bg-rose-500/5 rounded-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <AlertTriangle size={15} className="text-rose-400 shrink-0" />
        <span className="text-sm font-semibold text-rose-200" data-testid="expiry-alert-title">
          {expiredCount > 0 ? `${expiredCount} domain${expiredCount === 1 ? "" : "s"} EXPIRED · ` : ""}
          {expiring.length - expiredCount > 0 ? `${expiring.length - expiredCount} domain${expiring.length - expiredCount === 1 ? "" : "s"} expiring within 30 days` : ""}
        </span>
        <span className="text-[11px] text-rose-300/70">— renew at the registrar; sites go dark at expiry</span>
        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="expiry-filter-toggle"
            onClick={onToggleFilter}
            className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-sm border ${filterOn ? "bg-rose-500 text-zinc-950 border-rose-500" : "border-rose-500/40 text-rose-300 hover:border-rose-400"}`}
          >
            {filterOn ? <X size={11} /> : <CalendarClock size={11} />} {filterOn ? "Show all sites" : "Show only expiring"}
          </button>
          {refreshBtn}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mt-2">
        {expiring.slice(0, 12).map((s) => (
          <Link
            key={s.id}
            to={`/admin/sites/${s.id}?tab=domain`}
            data-testid={`expiry-chip-${s.id}`}
            className={`inline-flex items-center gap-1.5 mono text-[11px] px-2 py-0.5 rounded-sm border hover:brightness-125 ${(EXPIRY_STYLE[s.expiry.level] || EXPIRY_STYLE.critical).chip}`}
            title={`${s.name} · expires ${(s.expiry.expires_at || "").slice(0, 10)}${s.expiry.source ? ` · via ${s.expiry.source}` : ""}`}
          >
            {s.domain} <b>{daysLabel(s.expiry.days_left)}</b>{s.expiry.auto_renew === true && <RotateCw size={10} className="text-emerald-400" title="auto-renew ON" />}
          </Link>
        ))}
        {expiring.length > 12 && <span className="text-[11px] text-rose-300/70">+{expiring.length - 12} more</span>}
      </div>
      {msg && <div className="mt-2 text-[11px] mono text-zinc-400" data-testid="expiry-msg">{msg}</div>}
    </div>
  );
}

function EmailHealthBadge({ health }) {
  const { provider, status } = health || {};
  const cfg = {
    verified: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
    pending: "text-amber-400 border-amber-500/30 bg-amber-500/10",
    none: "text-zinc-500 border-zinc-700",
  }[status] || "text-zinc-500 border-zinc-700";
  const label = status === "verified" ? "verified" : status === "pending" ? "pending" : "no email";
  const providerLabel = provider === "infobip" ? "Infobip" : "SparkPost";
  const tip = {
    verified: `Email delivery (${providerLabel}): domain verified — DKIM/SPF valid. This is unrelated to whether the site is live.`,
    pending: `Email delivery (${providerLabel}): connected but not yet verified — DKIM/SPF records still propagating/awaiting verification. Independent of the site being live or having content. Open the site's Email tab → Verify to re-check.`,
    none: `No email provider connected for this domain. Independent of the site being live.`,
  }[status] || `Email · ${providerLabel} · ${status}`;
  return (
    <span
      data-testid="email-health-badge"
      title={tip}
      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm border shrink-0 ${cfg}`}
    >
      <Mail size={9} /> {label === "pending" ? "email pending" : label}
    </span>
  );
}

const STEP_ORDER = ["domain", "cloudflare", "email", "content"];
const STEP_LABELS = { domain: "Domain / NS", cloudflare: "Cloudflare", email: "Email", content: "Content" };
const STEP_STYLE = {
  ready: { dot: "#10b981", chip: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10", Icon: CheckCircle2 },
  pending: { dot: "#f59e0b", chip: "text-amber-400 border-amber-500/30 bg-amber-500/10", Icon: Clock },
  failed: { dot: "#ef4444", chip: "text-rose-400 border-rose-500/30 bg-rose-500/10", Icon: AlertCircle },
};

function ReadinessDots({ r, expanded, onToggle }) {
  if (!r) return null;
  return (
    <button
      data-testid="readiness-toggle"
      onClick={onToggle}
      className="inline-flex items-center gap-1.5 mt-1.5 group"
      title="Domain rollout progress — click to expand"
    >
      {STEP_ORDER.map((k) => (
        <span
          key={k}
          title={`${STEP_LABELS[k]}: ${r.steps[k]}`}
          className="inline-block rounded-full"
          style={{ width: 7, height: 7, background: (STEP_STYLE[r.steps[k]] || STEP_STYLE.pending).dot }}
        />
      ))}
      <span className="text-[10px] text-zinc-500 group-hover:text-zinc-300 ml-0.5 inline-flex items-center gap-0.5">
        {r.overall === "ready" ? "live" : r.overall === "failed" ? "needs attention" : "setting up"}
        {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
      </span>
    </button>
  );
}

function ReadinessPanel({ r, rechecking, onRecheck, error }) {
  return (
    <div data-testid="readiness-panel" className="px-4 py-3 bg-zinc-900/40 border-l-2 border-amber-500/40">
      <div className="flex items-center gap-2.5 flex-wrap">
        {STEP_ORDER.map((k, i) => {
          const st = STEP_STYLE[r.steps[k]] || STEP_STYLE.pending;
          const Icon = st.Icon;
          const count = k === "content" && r.content_count ? ` · ${r.content_count}` : "";
          return (
            <React.Fragment key={k}>
              <span data-testid={`readiness-step-${k}`} className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-sm border ${st.chip}`}>
                <Icon size={12} /> {STEP_LABELS[k]}{count}
              </span>
              {i < STEP_ORDER.length - 1 && <span className="text-zinc-700 select-none">→</span>}
            </React.Fragment>
          );
        })}
        <button
          data-testid="readiness-recheck"
          onClick={onRecheck}
          disabled={rechecking}
          className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-sm border border-sky-600 bg-sky-600/10 text-sky-300 hover:bg-sky-600/25 disabled:opacity-50"
          title="Run a live re-verify for just this domain"
        >
          <RefreshCw size={12} className={rechecking ? "animate-spin" : ""} /> {rechecking ? "Re-checking…" : "Re-check now"}
        </button>
      </div>
      {error && <div data-testid="readiness-recheck-error" className="mt-2 text-[11px] mono text-rose-400">{error}</div>}
    </div>
  );
}
